import * as React from 'react';
import { TFunction } from 'i18next';
import {
  AfterRenderQuestionEvent,
  CompletingEvent,
  CurrentPageChangingEvent,
  LoadChoicesFromServerEvent,
  UpdateQuestionCssClassesEvent,
  ValueChangedEvent,
  VariableChangedEvent
} from 'survey-core/typings/survey-events-api';
import { debounce, isUndefined, omit, pick } from 'lodash';
import { getErrorMessage } from './messages';
import { handleFormCompletion } from './complete';
import { IProductsResponse, ITaxRate } from '../types/products';
import { ItemValue, Model } from 'survey-core';
import { TYPE as MuiTextFieldType } from '../components/questionTypes/MuiTextField';
import { TYPE as MuiRadioGroupType } from '../components/questionTypes/MuiRadioGroup';
import { TYPE as MuiSelectType } from '../components/questionTypes/MuiSelect';
import { TYPE as MuiSubscriptionsType } from '../components/questionTypes/MuiSubscriptions';
import { SELF_SERVE_VALUE, TYPE as MuiAdditionalProductsType } from '../components/questionTypes/MuiAdditionalProducts';
import { TYPE as MuiPhoneInputType } from '../components/questionTypes/MuiPhoneInput';
import { TYPE as MuiDiscountType } from '../components/questionTypes/MuiDiscount';
import { TYPE as MuiCountriesType } from '../components/questionTypes/MuiCountries';
import { TYPE as MuiAddressType } from '../components/questionTypes/MuiAddress';
import { TYPE as MuiCompanySearchType } from '../components/questionTypes/MuiCompanySearch';
import { TYPE as MuiCheckboxType } from '../components/questionTypes/MuiCheckbox';
import { TYPE as MuiListType } from '../components/questionTypes/MuiList';
import { TYPE as MuiMembershipType } from '../components/questionTypes/MuiMembership';
import { TYPE as MuiPrivacyConsentType } from '../components/questionTypes/MuiPrivacyConsent';
import { updateLead } from './events';
import { AppStateFormData } from '../contexts/AppState';
import { getShouldUpdateLead } from './customFunctions';
import { CompanyLocation } from '../types/enums';
import { getPurchaseInfo } from './gtm';
import { PaymentMethod } from '@pec/aion-ui-core/enums/Subscription';

type FormState = {
  data: any;
  page: number;
  taxRate?: ITaxRate | null;
};

const questionCssClassesMap = {
  [MuiTextFieldType]: ' mui-text-field',
  [MuiRadioGroupType]: ' mui-radio-group',
  [MuiSelectType]: ' mui-select',
  [MuiSubscriptionsType]: ' mui-radio-group mui-subscriptions',
  [MuiAdditionalProductsType]: ' mui-radio-group mui-additional-products',
  [MuiPhoneInputType]: ' mui-phone-input',
  [MuiDiscountType]: ' mui-discount',
  [MuiCountriesType]: ' mui-countries',
  [MuiAddressType]: ' mui-address',
  [MuiCompanySearchType]: ' mui-company-search',
  [MuiCheckboxType]: ' mui-checkbox',
  [MuiListType]: ' mui-list',
  [MuiMembershipType]: ' mui-membership',
  [MuiPrivacyConsentType]: ' mui-privacy-consent'
};

export const FORM_STATE_KEY = 'signup:formState';
export const ACCOUNT_KEY = 'signup:account';
const STORED_DISCOUNT_DATA = 'signup:storedDiscountData';

const getStorageKey = (survey: Model, key: string) => {
  const type = survey.getVariable('signupType');

  return `${key}_${type}`;
};

const getFormState = (survey: Model): FormState => {
  try {
    const key = getStorageKey(survey, FORM_STATE_KEY);
    const stored = sessionStorage.getItem(key) || '{ "data": {}, "page": 0, "taxRate": null }';
    return JSON.parse(stored);
  } catch {
    return { data: {}, page: 0, taxRate: null };
  }
};
const saveFormState = (survey: Model, savePageOnly = false) => {
  try {
    const prevFormState = getFormState(survey);
    const subscriptionQuestion = survey.getQuestionByName('subscription');
    const isOrderPage = survey.activePage.containsElement(subscriptionQuestion);
    const additionalProduct = isUndefined(survey.data.additionalProduct)
      ? prevFormState.data.additionalProduct
      : survey.data.additionalProduct;
    const dataSharingConsent = survey.data.dataSharingConsent;
    const taxRate = survey.getVariable('taxRate');

    const stateToSave = {
      page: savePageOnly ? survey.currentPageNo : prevFormState.page,
      taxRate: taxRate || prevFormState.taxRate,
      data: savePageOnly
        ? prevFormState.data
        : {
            // When page is other that the "Order" page surveyjs removes values for 'subscription' and 'additionalProduct' automatically
            // Keep prev values in that case
            ...omit(
              survey.data,
              isOrderPage
                ? ['total', 'totalExplanation']
                : ['subscription', 'additionalProduct', 'total', 'totalExplanation']
            ),
            ...(isOrderPage ? { additionalProduct } : pick(prevFormState.data, ['subscription', 'additionalProduct'])),
            ...(isOrderPage ? { dataSharingConsent } : pick(prevFormState.data, ['dataSharingConsent']))
          }
    };
    sessionStorage.setItem(getStorageKey(survey, FORM_STATE_KEY), JSON.stringify(stateToSave));
  } catch {}
};

const saveFormStateDebounced = debounce(saveFormState, 500);

const applyProductsValues = (survey: Model) => {
  const formState = getFormState(survey);
  if (formState) {
    const { data } = formState;
    const subscription = survey.getQuestionByName('subscription');
    const subscriptionChoices = subscription.visibleChoices || [];

    subscription.clearValue();
    if (subscriptionChoices.find((choice: ItemValue) => choice.value === data.subscription)) {
      survey.setValue('subscription', data.subscription, '', false);
      survey.setValue('dataSharingConsent', data.dataSharingConsent, '', false);

      setTimeout(() => {
        // Additional product's visibility is dependent on subscription
        const filteredProducts = survey.getVariable('filteredProducts') || {};
        if (
          (Array.isArray(filteredProducts.additionalProduct) &&
            filteredProducts.additionalProduct.includes(data.additionalProduct)) ||
          data.additionalProduct === SELF_SERVE_VALUE
        ) {
          survey.setValue('additionalProduct', data.additionalProduct, '', false);
        }
        const appliedCode = survey.getQuestionByName('discountCode')?.value;
        const discount = sessionStorage.getItem(getStorageKey(survey, STORED_DISCOUNT_DATA));

        if (appliedCode && discount) {
          survey.setVariable('discount', JSON.parse(discount));
          survey.getQuestionByName('discountCode').setPropertyValue('appliedCode', appliedCode);
          survey.currentPage.setPropertyValue('forceUpdated', Date.now());
        }
      }, 0);
    }
  }
};

export const getAccount = () => {
  try {
    return Object.values(JSON.parse(sessionStorage.getItem(ACCOUNT_KEY) || '{}'))[0] as string;
  } catch {
    return;
  }
};

export const applyFormStateFromStorage = (survey: Model, updateFormData: (value: AppStateFormData) => void) => {
  const formState = getFormState(survey);
  if (formState) {
    const { page, data, taxRate } = formState;
    const countryParam = new URLSearchParams(window.location.search).get('country'); // Fix for dynamically added param
    const foundationParam = new URLSearchParams(window.location.search).get('foundation'); // Fix for dynamically added param
    let packageType = data.packageType || countryParam;
    if (!packageType && foundationParam) {
      packageType = CompanyLocation.GB;
    }
    // Values for ['subscription', 'additionalProduct'] will be applied after loading products
    survey.data = omit(data, ['companyName', 'subscription', 'additionalProduct']);
    survey.setValue('packageType', packageType);
    updateFormData({ packageType });
    survey.currentPageNo = page;

    // Restore taxRate from session storage to avoid race condition with async choice loading
    if (taxRate) {
      survey.setVariable('taxRate', taxRate);
    }

    const subscriptionQuestion = survey.getQuestionByName('subscription');
    const isOrderPage = survey.activePage.containsElement(subscriptionQuestion);

    survey.setValue('companyName', data.companyName);
    if (isOrderPage) survey.setVariable('isSubscriptionDisplayed', true);
  }
};

export const onUpdateQuestionCssClasses = (_: Model, options: UpdateQuestionCssClassesEvent) => {
  // According to the documentation this is the best way to override default CSS styles
  // https://surveyjs.io/form-library/documentation/api-reference/survey-data-model#onUpdateQuestionCssClasses
  const classes = options.cssClasses;
  classes.mainRoot += questionCssClassesMap[options.question.getType()];

  if (options.question.name === 'totalExplanation') {
    classes.mainRoot += ' total-price-explanation';
  }
};

export const onAfterRenderQuestion = (_: Model, options: AfterRenderQuestionEvent) => {
  const { parentClassName } = options.question;

  if (parentClassName) {
    const parent = options.htmlElement.parentElement;
    if (parent) {
      parent.className = parentClassName;
      parent.style.cssText = 'flex-shrink: 0';
    }
  }
};

export const onLoadChoicesFromServer = (taxRatesCacheRef: React.MutableRefObject<Record<string, ITaxRate | null>>) => (
  survey: Model,
  {
    question,
    choices,
    serverResult
  }: Omit<LoadChoicesFromServerEvent, 'serverResult'> & { serverResult: IProductsResponse }
) => {
  if (!survey.getVariable('isSubscriptionDisplayed')) {
    return;
  }
  if (choices.length > 0) {
    // Save choices to the variable, when order page is not active choices from main questions can not be accessible
    survey.setVariable('productsChoices', choices);
  }
  if (!isUndefined(serverResult.taxRate)) {
    survey.setVariable('taxRate', serverResult.taxRate);
  }

  setTimeout(() => {
    const { processedUrl } = question.choicesByUrl;

    // Cached response will not be returned in options.serverResult, store previously received taxRate results
    if (taxRatesCacheRef.current[processedUrl] !== undefined) {
      survey.setVariable('taxRate', taxRatesCacheRef.current[processedUrl]);
      survey.currentPage.setPropertyValue('forceUpdated', Date.now());
    } else if (!isUndefined(serverResult.taxRate)) {
      taxRatesCacheRef.current[processedUrl] = serverResult.taxRate;
    }

    if (choices.length === 0 && question.activeChoices.length > 0) {
      // Cached choices will not be returned in options.choices, read choices from question once it is rendered
      survey.setVariable('productsChoices', question.activeChoices);
    }
    applyProductsValues(survey);
  }, 0);
};

export const onSurveyComplete = (
  onFormStateChange: ({ isLoading, error }: { isLoading: boolean; error?: string }) => void,
  t: TFunction
) => async (survey: Model, options: CompletingEvent) => {
  let errorMessage = '';
  options.allow = false;
  onFormStateChange({ isLoading: true });

  try {
    const accountId = getAccount();
    if (!accountId) return;
    await updateLead(survey, survey.data, survey.currentPage.num, accountId, true);

    const { data } = await handleFormCompletion(survey, accountId);

    try {
      const purchaseInfo = getPurchaseInfo(survey, data.transactionId);
      if (purchaseInfo) {
        sessionStorage.setItem('purchaseInfo', JSON.stringify(purchaseInfo));
      }
    } catch {}

    if (survey.data.paymentMethod === PaymentMethod.BANK_TRANSFER && data.invoiceUrl) {
      window.location.href = `${window.location.origin}/signup/success?email=${encodeURIComponent(
        survey.data.email
      )}&userExists=${survey.getVariable('userExists')}&invoiceUrl=${encodeURIComponent(
        data.invoiceUrl
      )}&withoutPayment=true`;
    } else {
      window.location.href = data.checkoutUrl;
    }
  } catch (error) {
    const { status, data } = error.response;

    errorMessage = getErrorMessage(t);
    if (status === 400) {
      try {
        const { key } = JSON.parse(data);

        errorMessage = getErrorMessage(t, Array.isArray(key) ? key[0] : key);
      } catch {}
    }

    onFormStateChange({ error: errorMessage, isLoading: false });
  }
};

export const onCurrentPageChanged = (callback: () => void | undefined, focusFirstControl: (arg: boolean) => void) => (
  survey: Model
) => {
  const subscriptionQuestion = survey.getQuestionByName('subscription');
  const isOrderPage = survey.activePage.containsElement(subscriptionQuestion);
  const currentTaxRate = survey.getVariable('taxRate');
  survey.setVariable('isSubscriptionDisplayed', isOrderPage ? true : null);

  if (isOrderPage) {
    // Restore taxRate from session storage when returning to order page to avoid race condition
    if (!currentTaxRate) {
      const formState = getFormState(survey);
      if (formState.taxRate) {
        survey.setVariable('taxRate', formState.taxRate);
      }
    }
    applyProductsValues(survey);
  }

  saveFormStateDebounced.cancel();
  saveFormState(survey, true);
  callback && callback();
  focusFirstControl(true);
};

export const onCurrentPageChanging = (formDataChangedRef: React.MutableRefObject<boolean>) => (
  survey: Model,
  options: CurrentPageChangingEvent
) => {
  const shouldUpdateLead = getShouldUpdateLead(survey, formDataChangedRef.current, options);

  if (shouldUpdateLead) {
    formDataChangedRef.current = false;
    updateLead(
      survey,
      getFormState(survey).data || {}, // Read data from localStorage, additionalProduct value is deleted when order page is not active
      options.oldCurrentPage.num,
      getAccount()
    ).then(accountId => {
      sessionStorage.setItem(ACCOUNT_KEY, JSON.stringify({ [survey.data.email]: accountId }));
    });
  }
};

export const onValueChanged = (
  formDataChangedRef: React.MutableRefObject<boolean>,
  updateFormData: (value: AppStateFormData) => void
) => (survey: Model, options: ValueChangedEvent) => {
  if (['total', 'totalExplanation'].includes(options.name)) return;
  saveFormStateDebounced(survey);
  const formState = getFormState(survey);

  if (options.name === 'packageType') {
    survey.runExpression('resetCompanyDetails()');
    updateFormData({ packageType: options.value });
  }

  if (options.name === 'subscription') {
    const additionalProduct = survey.getQuestionByName('additionalProduct');
    const dataSharingConsent = survey.getQuestionByName('dataSharingConsent');
    additionalProduct && additionalProduct.clearValue();
    dataSharingConsent && dataSharingConsent.clearValue();
  }

  if (['totalEmployeeCount', 'applicationType', 'assessmentType'].includes(options.name)) {
    const newItem = { ...formState, data: { ...formState.data, subscription: null, additionalProduct: null } };
    sessionStorage.setItem(getStorageKey(survey, FORM_STATE_KEY), JSON.stringify(newItem));
  }

  if (['subscription', 'additionalProduct', 'dataSharingConsent'].includes(options.name) && !options.value) return;
  if (
    ['packageType', 'organizationType', 'country', 'totalEmployeeCount', 'applicationType', 'assessmentType'].includes(
      options.name
    ) &&
    survey.data.discountCode
  ) {
    survey.setValue('discountCode', null, '', false);
  }
  formDataChangedRef.current = true;
};

export const onVariableChanged = (setShowUserExistsDialog: (value: boolean) => void) => (
  _survey: Model,
  options: VariableChangedEvent
) => {
  if (options.name === 'showUserExistsDialog'.toLowerCase()) {
    setShowUserExistsDialog(Boolean(options.value));
  }
};
